import type { ComponentType } from 'react';
import { useOutreachCounts } from '@discovr/features';
import type { OutreachCounts } from '@discovr/types';
import { PhaseSubTabs } from '../parts/PhaseSubTabs';
import { RosterSearchSort } from '../parts/RosterSearchSort';
import type { OutreachCrmView } from '../../search';
import { useCrmViewParam } from '../../useCrmViewParam';
import { CrmAllCreatorsTable } from './CrmAllCreatorsTable';
import { CrmConversationsTable } from './CrmConversationsTable';
import { CrmRatesTable } from './CrmRatesTable';
import type { CrmCreatorsListProps } from './useCrmListPaging';
import { useCrmSearchInput } from './useCrmSearchInput';

export const CRM_SEARCH_PLACEHOLDER = 'Search all creators';

interface ViewSpec {
  key: OutreachCrmView;
  label: string;
  count: (counts: OutreachCounts | undefined) => number | undefined;
  List: ComponentType<CrmCreatorsListProps>;
}

/** An older Go has no count for the new lists; the chip then shows its label alone rather than a false 0. */
const knownCount = (count: number | null | undefined): number | undefined => count ?? undefined;

/** Figma's three lists. Each chip counts with its list's own tab clause, so the chip and the table agree. */
const VIEWS: readonly ViewSpec[] = [
  { key: 'all', label: 'All creators', count: (counts) => knownCount(counts?.everyone), List: CrmAllCreatorsTable },
  { key: 'conversations', label: 'Conversations', count: (counts) => knownCount(counts?.conversations), List: CrmConversationsTable },
  { key: 'rates', label: 'Rates', count: (counts) => knownCount(counts?.withRates), List: CrmRatesTable },
];

/** The Creators tab: list chips and a server-side search over this campaign's creators, then the chosen list. */
export function CrmCreatorsTab({ campaignId }: { campaignId: string }) {
  const { view, setView } = useCrmViewParam();
  const { data: counts } = useOutreachCounts(campaignId);
  const search = useCrmSearchInput();
  const active = VIEWS.find((spec) => spec.key === view) ?? VIEWS[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PhaseSubTabs
          bare
          active={view}
          onChange={setView}
          tabs={VIEWS.map((spec) => ({ key: spec.key, label: spec.label, count: spec.count(counts) }))}
        />
        <RosterSearchSort
          placeholder={CRM_SEARCH_PLACEHOLDER}
          ariaLabel={CRM_SEARCH_PLACEHOLDER}
          query={search.input}
          onQueryChange={search.setInput}
          searchClassName="w-full sm:w-[273px]"
          inputClassName="rounded-xl"
        />
      </div>
      {active && <active.List campaignId={campaignId} query={search.query} onClearSearch={search.clear} />}
    </div>
  );
}
